// Selling prices from costs (pricing's BR-PRICING-001..003, BR-PRICING-021; working decisions W1–W3 of docs/sprints/sprint-04.md).
// A selling price is VAT-inclusive cents and a cost ex-VAT cents (W1); a percentage is whole basis points (10 000 = 100%).
// Proven by TC-MONEY-006..016 (docs/test-cases/db.md, `packages/money/test/pricing.test.ts`).
import type { Bp, Cents, RoundingRule } from "@visionx/contracts";
import { assertAtLeast, assertInt, assertSafe, floorDiv, roundHalfUp } from "./arith";

/**
 * The markup of BR-PRICING-002 (W2): the VAT-inclusive shelf price straight from the ex-VAT cost —
 * cost + cost × percentage ÷ 10 000 (the product half-up to the cent) + value, before any rounding rule.
 * R 490.00 at 400%: `markup(49000, 40000)` = 49 000 + 196 000 = 245 000, R 2 450.00. The value is VAT-inclusive cents
 * (Proposed, UC-PRICING-004). A negative cost, percentage or value is a `RangeError`.
 */
export function markup(cost: Cents, percentBp: Bp, valueCents: Cents = 0 as Cents): Cents {
  assertInt(cost, "cost"); assertInt(percentBp, "percentBp"); assertInt(valueCents, "valueCents");
  assertAtLeast(cost, 0, "cost"); assertAtLeast(percentBp, 0, "percentBp"); assertAtLeast(valueCents, 0, "valueCents");
  const product = cost * percentBp;
  assertSafe(product, "cost × percentage");
  const price = cost + roundHalfUp(product, 10_000) + valueCents;
  assertSafe(price, "price");
  return price as Cents;
}

/** Steps of the "nearest" and "up to" rules, in cents. */
const R1 = 100;
const R5 = 500;
const R10 = 1_000;

/** To the nearest multiple of the step, halves up; a price above R 0.00 never becomes R 0.00 — it takes one step instead. */
const nearest = (step: number) => (price: number): number => Math.max(roundHalfUp(price, step) * step, price > 0 ? step : 0);
/** Up to the next multiple of the step; a multiple stays. */
const upTo = (step: number) => (price: number): number => {
  const whole = floorDiv(price, step);
  return (price - whole * step > 0 ? whole + 1 : whole) * step;
};
/** Up to the next price ending in the given cents; a price already ending so stays. */
const upToEnding = (cents: number) => (price: number): number => (price === 0 ? 0 : price + ((cents - (price % 100) + 100) % 100));

/** Every rule of BR-PRICING-003, keyed by its name so that a rule the contract adds without a rounder here fails the build. */
const ROUNDERS: Readonly<Record<RoundingRule, (price: number) => number>> = {
  None: (price) => price,
  NearestR1: nearest(R1),
  UpToNearestR5: upTo(R5),
  UpToNearestR10: upTo(R10),
  NearestR10: nearest(R10),
  ToX99: upToEnding(99),
  ToX95: upToEnding(95),
};

/**
 * The rounding rules of BR-PRICING-003 (CAT-36; W3 adds `NearestR10`), applied to a price a markup computed — never to a typed
 * price, a cost, a tariff, a discount or VAT. R 0.00 stays R 0.00 under every rule; a price above R 0.00 never rounds to
 * R 0.00. Rounding a rounded price changes nothing. A negative price or an unknown rule is a `RangeError`.
 */
export function roundPrice(price: Cents, rule: RoundingRule): Cents {
  assertInt(price, "price");
  assertAtLeast(price, 0, "price");
  const round = Object.prototype.hasOwnProperty.call(ROUNDERS, rule) ? ROUNDERS[rule] : undefined;
  if (!round) throw new RangeError(`unknown rounding rule ${String(rule)}`);
  const rounded = round(price);
  assertSafe(rounded, "rounded price");
  return rounded as Cents;
}

/**
 * An ex-VAT figure with VAT added, half-up to the cent: `addVat(172000, 1500)` = 197 800 (R 1 720.00 → R 1 978.00). The
 * reverse of `exVat` for an ex-VAT figure — `exVat(addVat(e))` is always `e` — though not for a VAT-inclusive price
 * (TC-MONEY-016): which is why selling prices are kept VAT-inclusive (W1). A rate outside 0–10 000 bp is a `RangeError`.
 */
export function addVat(exclusive: Cents, rateBp: Bp): Cents {
  assertInt(exclusive, "exclusive"); assertInt(rateBp, "rateBp");
  if (rateBp < 0 || rateBp > 10_000) throw new RangeError(`rateBp must be 0 to 10 000 basis points, got ${rateBp}`);
  const product = exclusive * rateBp;
  assertSafe(product, "exclusive × rate");
  const inclusive = exclusive + roundHalfUp(product, 10_000);
  assertSafe(inclusive, "inclusive");
  return inclusive as Cents;
}
